/**
 * Theme carousels (single asset, defer-loaded via addon-scripts.liquid).
 *
 * - initializeSlider: transform + fade carousel for legacy free-samples markup
 *   (.free-samples-mobile / .free-samples-desktop, .carousel-inner, .carousel-item).
 *   No matching markup exists in the current theme; init exits early when absent.
 *
 * - initAccountDashboardKlevuRecs: horizontal scroll + pager for Klevu RECS on the
 *   account dashboard. Root: .account-dashboard__klevu-recs (wraps .klevu-recs + pager).
 *   Pager stays hidden until recommendation tiles have layout (offsetWidth) so controls do not appear first.
 */

function initializeSlider() {
  const container = getContainer();
  if (!container) {
    return;
  }
  const carousel = container.querySelector('.carousel-inner');
  const carouselControl = container.querySelector('.carousel__control');
  const items = container.querySelectorAll('.carousel-item');
  const pButton = container.querySelector('.carousel-previous-button');
  const nButton = container.querySelector('.carousel-next-button');
  let currentSlideNumber = document.querySelector('#current-slider-number');
  let currentIndex = 0;

  function applyTransition(item, transitionStyles) {
    item.style.transition = transitionStyles;
  }

  function getContainer() {
    const screenWidth = window.innerWidth;
    if (screenWidth <= 1023) {
      return document.querySelector('.free-samples-mobile');
    }
    return document.querySelector('.free-samples-desktop');
  }

  function getItemsToShow() {
    const screenWidth = window.innerWidth;
    if (screenWidth >= 1512) {
      return 4;
    } else if (screenWidth >= 1024) {
      return 3;
    } else {
      return 1;
    }
  }

  function updateCarousel() {
    const itemsToShow = getItemsToShow();
    const itemWidth = 100 / itemsToShow;
    const offset = currentIndex * itemWidth;

    carousel.style.transform = `translateX(-${offset}%)`;
  }

  if (currentSlideNumber) {
    currentSlideNumber.innerText = getItemsToShow();
  }

  function fadeItemsOut(direction) {
    return new Promise((resolve) => {
      items.forEach((item) => {
        applyTransition(item, 'opacity 0.3s ease, transform 0.6s ease');
        item.style.opacity = 0;
        carousel.classList.replace('md:overflow-x-hidden', 'md:overflow-x-visible');

        if (direction === 'next') {
          toggleTranslateClasses(item, 'md:translate-x-6', 'md:-translate-x-6');
        } else if (direction === 'prev') {
          toggleTranslateClasses(item, 'md:-translate-x-6', 'md:translate-x-6');
        }
      });

      carousel.style.visibility = 'hidden';
      setTimeout(resolve, 600);
    });
  }

  function fadeItemsIn(direction) {
    carousel.classList.remove('md:-translate-x-6');

    items.forEach((item) => {
      applyTransition(item, 'opacity 0.6s ease 0.3s, transform 0.6s ease');
      item.style.opacity = 1;
      carousel.style.visibility = 'visible';

      if (direction === 'next') {
        setTimeout(() => {
          item.classList.remove('md:-translate-x-6');
        }, 300);
      } else if (direction === 'prev') {
        setTimeout(() => {
          item.classList.remove('md:translate-x-6', 'md:-translate-x-6');
        }, 300);
      }
    });
  }

  function toggleTranslateClasses(item, addClass, removeClass) {
    item.classList.remove(removeClass);
    item.classList.add(addClass);

    // Automatically revert after 300ms
    setTimeout(() => {
      item.classList.remove(addClass);
      item.classList.add(removeClass);
    }, 300);
  }

  async function slide(direction) {
    await fadeItemsOut(direction);
    const itemsToShow = getItemsToShow();
    const maxIndex = Math.max(0, items.length - itemsToShow);

    if (direction === 'next') {
      currentIndex = Math.min(currentIndex + itemsToShow, maxIndex);
      currentSlideNumber.innerText = Math.min(currentIndex + itemsToShow, items.length);
      pButton.classList.add("hover:scale-x-125");
      pButton.disabled = false;
      if (currentIndex + itemsToShow >= items.length) {
        nButton.classList.remove("hover:scale-x-125");
        nButton.disabled = true;
      }
    } else if (direction === 'prev') {
      if (currentIndex > 0) {
        currentIndex = Math.max(currentIndex - itemsToShow, 0);
      }
      currentSlideNumber.innerText = Math.min(currentIndex + itemsToShow, items.length);

      nButton.classList.add("hover:scale-x-125");
      nButton.disabled = false;

      if (currentIndex === 0) {
        pButton.classList.remove("hover:scale-x-125");
        pButton.disabled = true;
      }
    }

    updateCarousel();
    fadeItemsIn(direction);
  }

  function updateButtonVisibility() {
    const itemsToShow = getItemsToShow();
    if (carouselControl) {
      if (items.length <= itemsToShow) {
        carouselControl.style.display = 'none';
      } else {
        carouselControl.style.display = 'flex';
      }
    }
  }

  pButton.addEventListener('click', () => slide('prev'));
  nButton.addEventListener('click', () => slide('next'));

  window.addEventListener('resize', () => {
    updateCarousel();
    updateButtonVisibility();
  });

  updateCarousel();
  updateButtonVisibility();
}

const accountDashboardKlevuItemsHaveRenderableLayout = (orderedItems) =>
  orderedItems.some((el) => el.offsetWidth > 0)

function initAccountDashboardKlevuRecs() {
  const root = document.querySelector('.account-dashboard__klevu-recs')
  if (!root || root.dataset.accountDashboardKlevuPagerWired === 'true') return

  const pager = root.querySelector('[data-account-dashboard-klevu-pager]')
  const prevBtn = root.querySelector('[data-account-dashboard-klevu-prev]')
  const nextBtn = root.querySelector('[data-account-dashboard-klevu-next]')
  const statusEl = root.querySelector('[data-account-dashboard-klevu-status]')
  const template = root.dataset.accountDashboardKlevuPagerTemplate || '#CURRENT# of #TOTAL#'
  const itemsPerPage = Math.max(1, parseInt(root.dataset.accountDashboardKlevuItemsPerPage, 10) || 2)

  if (!pager || !prevBtn || !nextBtn || !statusEl) return

  root.dataset.accountDashboardKlevuPagerWired = 'true'

  const getScroller = () => root.querySelector('.kuRECSResultsInner')

  const getItemsOrdered = (scroller) => {
    const direct = [...scroller.querySelectorAll(':scope > .kuRECS-itemWrap')]
    if (direct.length > 0) return direct
    return [...scroller.querySelectorAll('.kuRECS-itemWrap')]
  }

  /** ScrollLeft that aligns this item’s start edge with the scroller’s visible start (subpixel-safe). */
  const getScrollLeftToAlignItemStart = (scroller, item) =>
    scroller.scrollLeft + (item.getBoundingClientRect().left - scroller.getBoundingClientRect().left)

  const getTotalPages = (items) => Math.max(1, Math.ceil(items.length / itemsPerPage))

  const getCurrentPage = (scroller, orderedItems) => {
    const totalPages = getTotalPages(orderedItems)
    if (totalPages <= 1) return 1

    const { scrollLeft } = scroller
    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth)
    if (maxScroll > 0 && scrollLeft >= maxScroll - 3) {
      return totalPages
    }

    const tol = Math.max(8, (orderedItems[0]?.offsetWidth || 0) * 0.15)
    let page = 1
    for (let p = 1; p <= totalPages; p++) {
      const idx = (p - 1) * itemsPerPage
      const el = orderedItems[idx]
      if (!el) break
      const start = getScrollLeftToAlignItemStart(scroller, el)
      if (start <= scrollLeft + tol) page = p
    }
    return page
  }

  const scrollToPage = (targetPage) => {
    const scroller = getScroller()
    if (!scroller) return

    const orderedItems = getItemsOrdered(scroller)
    const totalPages = getTotalPages(orderedItems)
    const p = Math.min(totalPages, Math.max(1, targetPage))
    const idx = (p - 1) * itemsPerPage
    const el = orderedItems[idx]
    if (!el) return

    const applyAlignedScroll = () => {
      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth)
      const raw = getScrollLeftToAlignItemStart(scroller, el)
      const nextLeft = Math.min(Math.max(0, Math.round(raw)), maxScroll)
      scroller.scrollTo({ left: nextLeft, behavior: 'instant' })
    }

    applyAlignedScroll()
    requestAnimationFrame(() => {
      applyAlignedScroll()
      updatePager()
    })
  }

  const formatStatus = (shownEndCount, totalProducts) =>
    template.replaceAll('#CURRENT#', String(shownEndCount)).replaceAll('#TOTAL#', String(totalProducts))

  const updatePager = () => {
    const scroller = getScroller()
    if (!scroller) {
      pager.hidden = true
      return
    }

    const orderedItems = getItemsOrdered(scroller)
    const totalPages = getTotalPages(orderedItems)
    const totalProducts = orderedItems.length

    if (totalProducts === 0) {
      pager.hidden = true
      statusEl.textContent = ''
      return
    }

    if (totalPages <= 1) {
      pager.hidden = true
      statusEl.textContent = ''
      return
    }

    if (!accountDashboardKlevuItemsHaveRenderableLayout(orderedItems)) {
      pager.hidden = true
      statusEl.textContent = ''
      return
    }

    pager.hidden = false

    const currentPage = getCurrentPage(scroller, orderedItems)
    const shownEndCount = Math.min(currentPage * itemsPerPage, totalProducts)
    statusEl.textContent = formatStatus(shownEndCount, totalProducts)

    prevBtn.disabled = currentPage <= 1
    nextBtn.disabled = currentPage >= totalPages
  }

  const bindScroller = (scroller) => {
    if (!scroller || scroller.dataset.accountDashboardKlevuScrollBound === 'true') return
    scroller.dataset.accountDashboardKlevuScrollBound = 'true'
    scroller.addEventListener('scroll', updatePager, { passive: true })
    scroller.addEventListener('scrollend', updatePager, { passive: true })
  }

  const tryBindAndUpdate = (layoutRetry = 0) => {
    const scroller = getScroller()
    bindScroller(scroller)
    const orderedItems = scroller ? getItemsOrdered(scroller) : []
    const totalPages = getTotalPages(orderedItems)
    const needsLayoutBeforePager =
      orderedItems.length > 0 &&
      totalPages > 1 &&
      !accountDashboardKlevuItemsHaveRenderableLayout(orderedItems)

    if (needsLayoutBeforePager && layoutRetry < 40) {
      requestAnimationFrame(() => tryBindAndUpdate(layoutRetry + 1))
      return
    }

    updatePager()
  }

  prevBtn.addEventListener('click', () => {
    const scroller = getScroller()
    if (!scroller) return
    const orderedItems = getItemsOrdered(scroller)
    const cur = getCurrentPage(scroller, orderedItems)
    scrollToPage(cur - 1)
  })

  nextBtn.addEventListener('click', () => {
    const scroller = getScroller()
    if (!scroller) return
    const orderedItems = getItemsOrdered(scroller)
    const cur = getCurrentPage(scroller, orderedItems)
    scrollToPage(cur + 1)
  })

  const resizeObserver = new ResizeObserver(() => tryBindAndUpdate())
  resizeObserver.observe(root)

  const klevuHost = root.querySelector('.klevu-recs') || root
  const mutationObserver = new MutationObserver(() => tryBindAndUpdate())
  mutationObserver.observe(klevuHost, { childList: true, subtree: true })

  tryBindAndUpdate()
  window.addEventListener('resize', tryBindAndUpdate, { passive: true })
}

// Initialize cart for the first time
document.addEventListener(
  'DOMContentLoaded',
  () => {
    initializeSlider()
    initAccountDashboardKlevuRecs()
  },
  { once: true }
);

// Listen for custom event to reinitialize cart
document.addEventListener('cartUpdated', () => {
  initializeSlider();
});
